/**
 * opportunities/editOpportunity/useRecordDocs.js - the record's Doc Hub
 * files and comment count (the tab badge, the linked files, the floating
 * comments), kept live for other people's changes.
 * Moved verbatim out of EditOpportunityModal.jsx; the editor passes in the
 * values it reads.
 */
import { useState, useCallback, useEffect } from 'react';
import { matchRecord, matchKind, useLiveRefresh } from '../../../live/useLive';

export function useRecordDocs({ createdId, opp, isCreate, base }) {
  // Floating comments post-it: auto-pops on the right when this opp already has
  // comments; the footer "Comment" button toggles it. It's rendered OUTSIDE the
  // Modal (fixed position) so it never steals width from the form. On a page
  // it waits for the button (the Doc Hub tab has the comments too).
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  // Doc Hub data: the tab badge (notes + files) and the Summary's linked
  // files need the attachment list before the tab is ever opened.
  const recordId = createdId || (opp && opp.id) || null;
  const [docs, setDocs] = useState([]);
  const loadDocs = useCallback(async () => {
    if (isCreate || !opp?.id) return;
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(opp.id)}/attachments`);
      if (r.ok) setDocs(await r.json());
    } catch {
      /* offline/dev */
    }
  }, [isCreate, opp?.id, base]);
  useEffect(() => {
    setDocs([]);
    loadDocs();
  }, [loadDocs]);
  const loadCommentCount = useCallback(
    async (pop) => {
      if (isCreate || !opp?.id) return;
      try {
        const r = await fetch(`${base}/projects/${opp.id}/comments`);
        const list = r.ok ? await r.json() : [];
        if (!Array.isArray(list)) return;
        setCommentCount(list.length);
        if (pop && list.length > 0) setShowComments(true); // pop up when a comment exists
      } catch {
        /* offline/dev */
      }
    },
    [isCreate, opp?.id, base]
  );
  // Someone else's file or note on this record (src/live): the badge and the
  // linked files follow without a refresh, as they did on the project page.
  const docParts = matchRecord(base, recordId, ['attachments', 'folders', 'comments']);
  const anyFile = matchKind(base, 'attachments');
  useLiveRefresh(
    null,
    () => {
      loadDocs();
      loadCommentCount(false);
    },
    { match: (e) => !!recordId && (docParts(e) || anyFile(e)) }
  );
  const docHubCount = commentCount + docs.length;
  return { recordId, loadCommentCount, docHubCount, docs, setCommentCount, loadDocs, setShowComments, commentCount, showComments };
}
